import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Select,
  DatePicker,
  Button,
  Typography,
  message,
  Statistic,
  Row,
  Col,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';

const { Title, Text } = Typography;

const formatHorasMinutos = (horas) => {
  const totalMinutos = Math.round(Number(horas || 0) * 60);
  const horasEnteras = Math.floor(totalMinutos / 60);
  const minutos = totalMinutos % 60;

  if (horasEnteras > 0 && minutos > 0) {
    return `${horasEnteras} h ${minutos} min`;
  }

  if (horasEnteras > 0) {
    return `${horasEnteras} h`;
  }

  return `${minutos} min`;
};

const ClasesPorProfesor = () => {
  const [profesores, setProfesores] = useState([]);
  const [loadingProfs, setLoadingProfs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    const load = async () => {
      setLoadingProfs(true);
      try {
        const res = await apiClient.get('/profesores');
        if (res.ok) {
          const data = await res.json();
          setProfesores(Array.isArray(data) ? data : []);
        } else {
          message.error('No se pudieron cargar los profesores');
        }
      } catch (e) {
        message.error('No se pudieron cargar los profesores');
      } finally {
        setLoadingProfs(false);
      }
    };
    load();
  }, []);

  const onFinish = async (values) => {
    const rango = values.periodo;
    if (!rango || !rango[0] || !rango[1]) {
      message.warning('Seleccione fecha desde y hasta');
      return;
    }
    const [desde, hasta] = rango;
    const profesorId = values.profesorId;
    setLoading(true);
    setResultado(null);
    try {
      const fd = desde.format('YYYY-MM-DD');
      const fh = hasta.format('YYYY-MM-DD');
      const qs = new URLSearchParams({
        profesorId: String(profesorId),
        fechaDesde: fd,
        fechaHasta: fh,
      });
      const res = await apiClient.get(`/turnos/conteo-clases-profesor?${qs.toString()}`);
      let body = {};
      try {
        body = await res.json();
      } catch (_) {
        body = {};
      }
      if (!res.ok) {
        message.error(body.message || 'Error al consultar');
        return;
      }
      setResultado(body);
    } catch (e) {
      message.error('Error al consultar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div
        style={{
          width: '100%',
          minHeight: '100%',
          padding: 24,
          display: 'flex',
          justifyContent: 'center',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ width: '100%', maxWidth: 720 }}>
        <Title level={3} style={{ marginTop: 0 }}>
          Clases por profesor
        </Title>
        <Text type="secondary">
          Elija el profesor y el período. Se cuentan las ocurrencias de la agenda, las horas trabajadas
          y las clases canceladas sin sumarlas como horas trabajadas.
        </Text>
        <Card style={{ marginTop: 16 }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              periodo: [dayjs().startOf('month'), dayjs().endOf('month')],
            }}
          >
            <Form.Item
              name="profesorId"
              label="Profesor"
              rules={[{ required: true, message: 'Seleccione un profesor' }]}
            >
              <Select
                showSearch
                placeholder="Profesor"
                loading={loadingProfs}
                optionFilterProp="label"
                options={profesores.map((p) => ({
                  value: p.pro_id,
                  label: `${p.pro_nombre || ''} ${p.pro_apellido || ''}`.trim() || `ID ${p.pro_id}`,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="periodo"
              label="Fecha desde / hasta"
              rules={[{ required: true, message: 'Seleccione el período' }]}
            >
              <DatePicker.RangePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading}>
                Consultar
              </Button>
            </Form.Item>
          </Form>
        </Card>
        {resultado && (
          <Card style={{ marginTop: 16 }} title="Resultado">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Statistic title="Clases trabajadas" value={resultado.total} />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Statistic
                  title="Horas trabajadas"
                  value={formatHorasMinutos(resultado.horas_trabajadas)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Statistic title="Clases canceladas" value={resultado.clases_canceladas || 0} />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Statistic
                  title="Horas canceladas"
                  value={formatHorasMinutos(resultado.horas_canceladas)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Statistic title="Horarios fijos (por día en el rango)" value={resultado.clases_fijas} />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Statistic title="Eventos" value={resultado.clases_evento} />
              </Col>
            </Row>
          </Card>
        )}
        </div>
      </div>
    </MainLayout>
  );
};

export default ClasesPorProfesor;
